import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@evogard/ui';
import {
  SENDGRID_SETTING_KEYS,
  SENDGRID_SETTING_PREFIX,
  canAccess,
} from '@evogard/domain';
import { api, errorMessage } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { SettingField } from './TenantSettingsScreen.js';

/**
 * A Configuração da base, com a mesma chave de consulta da tela de
 * Configuração e dos painéis da CAF — abrir um não busca de novo o outro.
 */
function useTenantSettings(tenantId: number | undefined) {
  return useQuery({
    queryKey: ['plataforma', 'configuracao-da-base', tenantId],
    queryFn: async () => {
      const r = await api.platform.listSetting();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar o remetente.'));
      }
      return r.body.keys;
    },
    enabled: tenantId !== undefined,
  });
}

/**
 * A base tem remetente? `null` enquanto não se sabe — e quem não pode ler a
 * Configuração da base também não sabe, e a lista não afirma que falta.
 */
export function useSendgridSenderMissing(tenantId: number | undefined): boolean | null {
  const settings = useTenantSettings(tenantId);
  if (!settings.data) return null;
  const sender = settings.data.find((s) => s.key === SENDGRID_SETTING_KEYS.sender);
  return typeof sender?.value !== 'string' || sender.value.trim() === '';
}

/**
 * O remetente do e-mail da base (27/10/2026): o endereço e o nome que o
 * SendGrid põe no `from`. São chaves da Configuração da base (`sendgrid.*`),
 * com o mesmo campo e a mesma validação de lá — e a mesma permissão,
 * `platform.tenants:editar`, porque valem para a praça inteira.
 */
export function SendgridSenderPanel({ tenantId }: { tenantId: number | undefined }) {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const canEdit = canAccess(context?.permissions ?? [], 'platform.tenants', 'editar');
  const key = ['plataforma', 'configuracao-da-base', tenantId];
  const query = useTenantSettings(tenantId);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-texto-suave">
        O e-mail de confirmação de conta — o convite de primeiro acesso de quem é
        cadastrado como usuário — sai deste endereço. Ele precisa estar verificado no
        SendGrid, em Settings › Sender Authentication — o que não estiver, o SendGrid
        recusa.
      </p>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {query.data
        ?.filter((s) => s.key.startsWith(SENDGRID_SETTING_PREFIX))
        .map((setting) => (
          <SettingField
            key={setting.key}
            setting={setting}
            canEdit={canEdit}
            toWrite={(keys) => client.setQueryData(key, keys)}
          />
        ))}
      {!canEdit && (
        <p className="text-xs text-texto-suave">
          Trocar o remetente pede a permissão de editar a configuração da base.
        </p>
      )}
    </div>
  );
}
